import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { lookupPincode } from "@/lib/pincode";
import { Sun, Sparkles, MapPin } from "lucide-react";
import { useTranslation } from "@/context/LanguageContext";

export default function LeadDialog({
  open,
  onOpenChange,
  title,
  subtitle,
  context = {},
  source = "website",
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    pincode: "",
    segment: "residential",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const detected = React.useMemo(() => lookupPincode(form.pincode), [form.pincode]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error(t("lead.toast_required"));
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...context,
        ...form,
        email: form.email || null,
        source,
        pincode: form.pincode || context.pincode || null,
        state: detected?.state || context.state || null,
        discom: detected?.discom || context.discom || null,
      };
      await api.post("/leads", payload);
      setDone(true);
      toast.success(t("lead.toast_ok"));
      setTimeout(() => {
        onOpenChange(false);
        setDone(false);
        setForm({
          name: "", phone: "", email: "", pincode: "", segment: "residential", message: "",
        });
      }, 1600);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("lead.toast_fail"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="lead-dialog"
        className="sm:max-w-[520px] p-0 overflow-hidden rounded-3xl"
      >
        <div className="bg-green-700 text-white p-6 relative">
          <div className="absolute -top-6 -right-6 w-28 h-28 rounded-full bg-yellow-400/30 blur-2xl" />
          <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-3 py-1 text-[11px] tracking-widest uppercase">
            <Sparkles className="w-3.5 h-3.5 text-yellow-300" /> {t("lead.badge")}
          </div>
          <DialogHeader className="mt-3">
            <DialogTitle className="text-white font-display font-extrabold text-2xl">
              {title || t("lead.title_default")}
            </DialogTitle>
            <DialogDescription className="text-green-50/90">
              {subtitle || t("lead.subtitle_default")}
            </DialogDescription>
          </DialogHeader>
        </div>

        {done ? (
          <div className="p-8 text-center" data-testid="lead-success">
            <Sun className="w-14 h-14 text-yellow-500 mx-auto sun-pulse" />
            <div className="font-display font-extrabold text-2xl mt-3 text-stone-900">
              {t("lead.thanks_title")}
            </div>
            <p className="text-stone-600 mt-2">{t("lead.thanks_desc")}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label htmlFor="name">{t("lead.full_name")}</Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder={t("lead.name_ph")}
                  required
                  data-testid="lead-input-name"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="phone">{t("lead.phone")}</Label>
                <Input
                  id="phone"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  placeholder={t("lead.phone_ph")}
                  required
                  data-testid="lead-input-phone"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="pincode">{t("lead.pincode")}</Label>
                <Input
                  id="pincode"
                  value={form.pincode}
                  onChange={(e) =>
                    update("pincode", e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  placeholder="560001"
                  inputMode="numeric"
                  data-testid="lead-input-pincode"
                  className="mt-1.5"
                />
                {detected ? (
                  <div
                    className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5"
                    data-testid="lead-discom-detected"
                  >
                    <MapPin className="w-3 h-3" />
                    {detected.state} · {detected.discom}
                  </div>
                ) : null}
              </div>
              <div className="col-span-2">
                <Label htmlFor="email">{t("lead.email")}</Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder={t("lead.email_ph")}
                  data-testid="lead-input-email"
                  className="mt-1.5"
                />
              </div>
              <div className="col-span-2">
                <Label>{t("lead.i_need")}</Label>
                <RadioGroup
                  value={form.segment}
                  onValueChange={(v) => update("segment", v)}
                  className="mt-2 grid grid-cols-3 gap-2"
                >
                  <label
                    className={`flex items-center gap-2 rounded-xl border px-3 py-2 cursor-pointer ${
                      form.segment === "residential"
                        ? "border-green-600 bg-green-50"
                        : "border-stone-200"
                    }`}
                  >
                    <RadioGroupItem value="residential" data-testid="lead-segment-res" />
                    <span className="text-sm font-medium">{t("lead.seg_home")}</span>
                  </label>
                  <label
                    className={`flex items-center gap-2 rounded-xl border px-3 py-2 cursor-pointer ${
                      form.segment === "commercial"
                        ? "border-orange-500 bg-orange-50"
                        : "border-stone-200"
                    }`}
                  >
                    <RadioGroupItem value="commercial" data-testid="lead-segment-com" />
                    <span className="text-sm font-medium">{t("lead.seg_commercial")}</span>
                  </label>
                  <label
                    className={`flex items-center gap-2 rounded-xl border px-3 py-2 cursor-pointer ${
                      form.segment === "agriculture"
                        ? "border-green-600 bg-green-50"
                        : "border-stone-200"
                    }`}
                  >
                    <RadioGroupItem value="agriculture" data-testid="lead-segment-agri" />
                    <span className="text-sm font-medium">{t("lead.seg_farm")}</span>
                  </label>
                </RadioGroup>
              </div>
              <div className="col-span-2">
                <Label htmlFor="message">{t("lead.anything_else")}</Label>
                <Textarea
                  id="message"
                  rows={2}
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  placeholder={t("lead.msg_ph")}
                  data-testid="lead-input-message"
                  className="mt-1.5"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="lead-submit"
              className="w-full h-12 rounded-full bg-green-600 hover:bg-green-700 text-white font-semibold text-base"
            >
              {submitting ? t("lead.submitting") : t("lead.submit")}
            </Button>
            <p className="text-[11px] text-stone-500 text-center">{t("lead.consent")}</p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
